<template>
  <div class="wa-tpl-picker">
    <!-- Loading -->
    <div v-if="loading" class="wa-tpl-picker__loading">
      <i class="el-icon-loading"></i> Cargando plantillas...
    </div>

    <!-- Sin plantillas aprobadas -->
    <div v-else-if="!templates.length" class="wa-tpl-picker__empty">
      <i class="el-icon-warning-outline"></i>
      <template v-if="source === 'marketing'">
        {{ marketingEmptyMessage }}
        <a
          v-if="!marketingStatus.error"
          href="#"
          @click.prevent="goToMarketing"
          class="wa-tpl-picker__link"
          >Ir a Marketing →</a
        >
      </template>
      <template v-else>
        No hay plantillas aprobadas por Meta.
        <a href="#" @click.prevent="goToTemplates" class="wa-tpl-picker__link"
          >Ir a Plantillas →</a
        >
      </template>
    </div>

    <!-- Selector -->
    <template v-else>
      <!-- Aviso de plantillas mock (modo prueba) -->
      <div v-if="usingMockTemplates" class="wa-tpl-picker__mock-banner">
        <i class="el-icon-info"></i>
        Plantillas de muestra (modo prueba). En producción se cargarán las
        aprobadas por Meta de tu cuenta.
      </div>

      <!-- Dropdown de plantilla -->
      <div class="wa-tpl-picker__field">
        <el-select
          v-model="selectedId"
          placeholder="Selecciona una plantilla..."
          size="small"
          style="width:100%;"
          @change="onTemplateChange"
        >
          <el-option
            v-for="tpl in templates"
            :key="tpl.id"
            :label="tpl.name"
            :value="tpl.id"
          >
            <div class="wa-tpl-picker__option">
              <span class="wa-tpl-picker__option-name">{{ tpl.name }}</span>
              <el-tag
                size="mini"
                type="info"
                style="margin-left:auto;flex-shrink:0;"
              >
                {{ tpl.category }}
              </el-tag>
            </div>
          </el-option>
        </el-select>
        <!-- Acción para marcar como predefinida del módulo -->
        <div v-if="moduleType && selectedId" class="wa-tpl-picker__default-row">
          <span v-if="isCurrentDefault" class="wa-tpl-picker__default-badge">
            <i class="el-icon-star-on"></i> Predefinida para este módulo
            <a
              href="#"
              @click.prevent="unsetDefault"
              class="wa-tpl-picker__default-link"
              >Quitar</a
            >
          </span>
          <a
            v-else
            href="#"
            @click.prevent="setAsDefault"
            class="wa-tpl-picker__default-action"
          >
            <i class="el-icon-star-off"></i> Marcar como predefinida
          </a>
        </div>
      </div>

      <!-- Preview del mensaje seleccionado -->
      <div v-if="selectedTemplate" class="wa-tpl-picker__preview-wrap">
        <div class="wa-tpl-picker__preview-caption">
          Vista previa con datos de ejemplo. Cada destinatario verá los suyos.
        </div>
        <div class="wa-tpl-picker__preview">
          <div
            v-if="selectedTemplate.header_content"
            class="wa-tpl-picker__preview-header"
          >
            {{ selectedTemplate.header_content }}
          </div>
          <div class="wa-tpl-picker__preview-body">{{ previewBody }}</div>
          <div
            v-if="selectedTemplate.footer"
            class="wa-tpl-picker__preview-footer"
          >
            {{ selectedTemplate.footer }}
          </div>
          <div
            v-if="selectedTemplate.buttons && selectedTemplate.buttons.length"
            class="wa-tpl-picker__preview-btns"
          >
            <span
              v-for="(btn, i) in selectedTemplate.buttons"
              :key="i"
              class="wa-tpl-picker__preview-btn"
              >{{ btn.text }}</span
            >
          </div>
        </div>
      </div>

      <!-- Variables de la plantilla -->
      <div v-if="variableFields.length" class="wa-tpl-picker__vars">
        <label class="wa-tpl-picker__label">Variables del mensaje</label>
        <div class="wa-tpl-picker__vars-hint">
          Cada variable se reemplaza automáticamente por el dato real de cada
          destinatario al enviar.
        </div>

        <div
          v-for="(field, i) in variableFields"
          :key="i"
          class="wa-tpl-picker__var-row"
        >
          <span class="wa-tpl-picker__var-tag" v-text="varLabel(i)"></span>
          <div class="wa-tpl-picker__var-controls">
            <div v-if="field.label" class="wa-tpl-picker__var-name">
              {{ field.label }}
            </div>
            <el-select
              v-model="variableFields[i].value"
              size="small"
              style="width:100%;"
              filterable
              @change="emitValue"
            >
              <el-option-group label="Datos del sistema">
                <el-option
                  v-for="t in availableTokens"
                  :key="t.value"
                  :label="t.label"
                  :value="t.value"
                >
                  <span style="float:left;">{{ t.label }}</span>
                  <span
                    style="float:right;color:#9ca3af;font-size:11px;font-family:monospace;"
                    >{{ t.value }}</span
                  >
                </el-option>
              </el-option-group>
              <el-option-group label="Otro">
                <el-option label="Valor personalizado..." value="__custom__" />
              </el-option-group>
            </el-select>

            <!-- Input cuando es valor personalizado -->
            <el-input
              v-if="variableFields[i].value === '__custom__'"
              v-model="variableFields[i].customValue"
              :placeholder="
                'Valor fijo para todos los destinatarios (ej: ' +
                  (field.label || 'texto') +
                  ')'
              "
              size="small"
              class="wa-tpl-picker__var-custom"
              @input="emitValue"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import configService from "@/modules/dashboard/configurations/services/configService";
import reminderService from "@/modules/dashboard/reminders/services/ReminderService";

// Tokens del sistema. El backend reconoce estos placeholders y los reemplaza
// con los datos reales de cada destinatario / documento al enviar.
const SYSTEM_TOKENS = {
  common: [
    { value: "{{nombre_cliente}}", label: "Nombre del cliente" },
    { value: "{{empresa}}", label: "Tu empresa" },
    { value: "{{fecha}}", label: "Fecha actual" }
  ],
  quote: [
    { value: "{{numero_cotizacion}}", label: "Número de cotización" },
    { value: "{{monto}}", label: "Monto total" },
    { value: "{{fecha_vencimiento}}", label: "Fecha de vencimiento" },
    { value: "{{dias_vencida}}", label: "Días vencida" }
  ],
  invoice: [
    { value: "{{numero_factura}}", label: "Número de factura" },
    { value: "{{monto}}", label: "Monto total" },
    { value: "{{fecha_vencimiento}}", label: "Fecha de vencimiento" },
    { value: "{{dias_vencida}}", label: "Días vencida" }
  ],
  "credit-note": [
    { value: "{{numero_factura}}", label: "Número de factura asociada" },
    { value: "{{monto}}", label: "Monto" }
  ],
  "debit-note": [
    { value: "{{numero_factura}}", label: "Número de factura asociada" },
    { value: "{{monto}}", label: "Monto" }
  ],
  withholding: [
    { value: "{{numero_retencion}}", label: "Número de retención" },
    { value: "{{monto}}", label: "Monto" }
  ],
  liquidation: [
    { value: "{{numero_liquidacion}}", label: "Número de liquidación" },
    { value: "{{monto}}", label: "Monto" }
  ],
  remission: [
    { value: "{{numero_remision}}", label: "Número de guía" },
    { value: "{{destinatario}}", label: "Destinatario" }
  ]
};

// Mapeo heurístico: dado el label de una variable (ej: "Nombre cliente",
// "nombre_cliente", "{{nombre_cliente}}"), devuelve el token del sistema
// más probable. Soporta tanto labels descriptivos como nombres exactos.
function guessTokenFromLabel(label, tokens) {
  if (!label) return null;
  const norm = String(label)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[{}]/g, "");

  // Coincidencia exacta primero (cuando el label es exactamente el nombre del token)
  const exactMatch = tokens.find(
    t => t.value.replace(/[{}]/g, "") === norm.trim()
  );
  if (exactMatch) return exactMatch.value;

  // Coincidencias por palabras clave (orden importa: más específicas primero)
  const matchers = [
    { keys: ["cotizacion", "cotizaci"], token: "{{numero_cotizacion}}" },
    { keys: ["retencion"], token: "{{numero_retencion}}" },
    { keys: ["liquidacion"], token: "{{numero_liquidacion}}" },
    { keys: ["remision", "guia"], token: "{{numero_remision}}" },
    { keys: ["factura", "factur"], token: "{{numero_factura}}" },
    { keys: ["destinatario"], token: "{{destinatario}}" },
    { keys: ["nombre_cliente", "nombre cliente"], token: "{{nombre_cliente}}" },
    { keys: ["nombre", "cliente"], token: "{{nombre_cliente}}" },
    { keys: ["empresa", "negocio", "razon"], token: "{{empresa}}" },
    { keys: ["monto", "valor", "total", "precio"], token: "{{monto}}" },
    { keys: ["vencimiento", "vence"], token: "{{fecha_vencimiento}}" },
    { keys: ["dias", "vencida"], token: "{{dias_vencida}}" },
    { keys: ["fecha"], token: "{{fecha}}" }
  ];
  for (const m of matchers) {
    if (
      m.keys.some(k => norm.includes(k)) &&
      tokens.find(t => t.value === m.token)
    ) {
      return m.token;
    }
  }
  return null;
}

export default {
  name: "WhatsAppTemplatePicker",
  props: {
    value: { type: Object, default: null },
    moduleType: { type: String, default: "" },
    // "crm": plantillas de la conexión directa del CRM (envío de documentos, masivos).
    // "marketing": plantillas del WhatsApp del módulo de marketing (recordatorios de cobro).
    source: { type: String, default: "crm" }
  },
  data() {
    return {
      loading: true,
      templates: [],
      selectedId: null,
      defaultId: null, // ID guardado como predefinida en localStorage
      variableFields: [],
      marketingStatus: { connected: true, addon_active: true, error: false }
    };
  },
  computed: {
    selectedTemplate() {
      return this.templates.find(t => t.id === this.selectedId) || null;
    },
    localKey() {
      return this.moduleType ? `wa_tpl_${this.moduleType}` : null;
    },
    isCurrentDefault() {
      return this.defaultId && this.selectedId === this.defaultId;
    },
    availableTokens() {
      // Normalizar moduleType: el modal masivo usa plurales ("quotes",
      // "invoices") mientras que los drawers individuales usan singulares
      // ("quote", "invoice", "credit-note"). Aquí los unificamos.
      const moduleAliases = {
        quotes: "quote",
        invoices: "invoice",
        "credit-notes": "credit-note",
        "debit-notes": "debit-note",
        withholdings: "withholding",
        liquidations: "liquidation",
        remissions: "remission",
        clients: "common"
      };
      const key = moduleAliases[this.moduleType] || this.moduleType;
      const moduleTokens = SYSTEM_TOKENS[key] || [];
      // Combina tokens del módulo + comunes, sin duplicar values
      const all = [...moduleTokens, ...SYSTEM_TOKENS.common];
      const seen = new Set();
      return all.filter(t => {
        if (seen.has(t.value)) return false;
        seen.add(t.value);
        return true;
      });
    },
    marketingEmptyMessage() {
      const st = this.marketingStatus;
      if (st.error)
        return "No se pudieron cargar las plantillas de tu WhatsApp de Marketing. Intenta de nuevo en unos minutos.";
      if (!st.addon_active)
        return "Activa el módulo de Marketing (WhatsApp) para enviar recordatorios por WhatsApp.";
      if (!st.connected)
        return "Conecta tu WhatsApp en Marketing para usar plantillas en los recordatorios.";
      return "No hay plantillas aprobadas en tu WhatsApp de Marketing.";
    },
    usingMockTemplates() {
      // Las mock tienen IDs negativos
      return this.templates.length && this.templates[0].id < 0;
    },
    previewBody() {
      if (!this.selectedTemplate) return "";
      let body = this.selectedTemplate.body || "";
      this.variableFields.forEach((f, i) => {
        let display;
        if (f.value === "__custom__") {
          display = f.customValue || `[${f.label || "valor"}]`;
        } else if (f.value) {
          // Mostrar valor de ejemplo realista (lo que vería el destinatario)
          display = this.exampleValueForToken(f.value);
        } else {
          display = `{{${i + 1}}}`;
        }
        body = body.replace(`{{${i + 1}}}`, display);
      });
      return body;
    }
  },
  watch: {
    // Garantiza emit siempre que el ID seleccionado cambie por cualquier vía
    selectedId() {
      this.onTemplateChange();
    }
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    async loadTemplates() {
      this.loading = true;
      if (this.source === "marketing") {
        await this.loadMarketingTemplates();
        return;
      }
      const isDev = localStorage.getItem("wa_dev_mode") === "1";

      try {
        const res = await configService.getWhatsAppTemplates({
          status: "approved"
        });
        const all = res.data.data || [];
        this.templates = all.filter
          ? all.filter(t => t.status === "approved")
          : all;

        // En modo prueba: si no hay plantillas reales aprobadas, inyectar ejemplos
        if (isDev && !this.templates.length) {
          this.templates = this.getMockTemplates();
        }
      } catch (e) {
        this.templates = isDev ? this.getMockTemplates() : [];
      } finally {
        // Leer el default guardado para este módulo
        if (this.localKey) {
          const saved = parseInt(localStorage.getItem(this.localKey));
          if (saved && this.templates.find(t => t.id === saved)) {
            this.defaultId = saved;
          } else if (saved) {
            // Si el id guardado ya no existe (ej: plantillas mock cambiaron),
            // limpiar para no quedar en un estado inválido
            localStorage.removeItem(this.localKey);
          }
        }

        // Determinar pre-selección:
        // 1) default explícito guardado
        // 2) value prop (si el padre ya tenía una plantilla)
        // 3) plantilla más relevante para el módulo
        // 4) primera plantilla
        let preselectedId = this.defaultId;
        if (!preselectedId && this.value && this.value.templateId) {
          preselectedId = this.value.templateId;
        }
        if (!preselectedId && this.templates.length) {
          const suggested = this.suggestTemplateForModule();
          preselectedId = suggested ? suggested.id : this.templates[0].id;
        }

        this.selectedId = preselectedId;
        this.onTemplateChange();
        this.loading = false;
      }
    },

    async loadMarketingTemplates() {
      try {
        const res = await reminderService.getCollectionWhatsappTemplates();
        const d = (res.data && res.data.data) || {};
        this.marketingStatus = {
          connected: !!d.connected,
          addon_active: !!d.addon_active,
          error: !!d.unavailable
        };
        // Back-mkt identifica la plantilla por nombre + idioma (no tiene id del CRM)
        this.templates = (d.templates || []).map(t => ({
          id: `${t.name}::${t.language}`,
          name: t.name,
          language: t.language,
          category: t.category,
          status: "approved",
          body: t.body,
          variables: []
        }));
      } catch (e) {
        this.marketingStatus = {
          connected: false,
          addon_active: false,
          error: true
        };
        this.templates = [];
      } finally {
        let preselectedId =
          this.value && this.value.templateId ? this.value.templateId : null;
        if (
          preselectedId &&
          !this.templates.find(t => t.id === preselectedId)
        ) {
          preselectedId = null;
        }
        // Sin plantilla guardada no se sugiere ninguna: en cobros el usuario
        // la elige a mano (la primera aprobada suele ser de ventas).
        this.selectedId = preselectedId;
        this.onTemplateChange();
        this.loading = false;
      }
    },
    getMockTemplates() {
      // Plantillas de muestra para modo prueba — simulan plantillas aprobadas por Meta
      return [
        {
          id: -1,
          name: "cotizacion_emitida",
          category: "UTILITY",
          status: "approved",
          header_content: "Tu cotización está lista",
          body:
            "Hola {{1}}, tu cotización N° {{2}} por un valor de {{3}} ha sido emitida y enviada a tu correo. Cualquier consulta estamos a la orden.",
          footer: "Mydessk · Gestión de Negocios",
          buttons: [{ type: "URL", text: "Ver cotización" }],
          variables: ["nombre_cliente", "numero_cotizacion", "monto"]
        },
        {
          id: -2,
          name: "factura_emitida",
          category: "UTILITY",
          status: "approved",
          header_content: "Tu factura está lista",
          body:
            "Hola {{1}}, te informamos que la factura N° {{2}} por {{3}} ha sido emitida. Puedes descargarla desde el enlace adjunto. Gracias por tu preferencia.",
          footer: "Mydessk · Gestión de Negocios",
          buttons: [
            { type: "URL", text: "Ver factura" },
            { type: "QUICK_REPLY", text: "Tengo una consulta" }
          ],
          variables: ["nombre_cliente", "numero_factura", "monto"]
        },
        {
          id: -3,
          name: "nota_credito_emitida",
          category: "UTILITY",
          status: "approved",
          header_content: "Nota de crédito emitida",
          body:
            "Hola {{1}}, te informamos que se ha emitido una nota de crédito por {{2}} sobre la factura N° {{3}}. Quedamos atentos.",
          footer: "Mydessk · Gestión de Negocios",
          buttons: [{ type: "URL", text: "Ver nota de crédito" }],
          variables: ["nombre_cliente", "monto", "numero_factura"]
        },
        {
          id: -4,
          name: "nota_debito_emitida",
          category: "UTILITY",
          status: "approved",
          header_content: "Nota de débito emitida",
          body:
            "Hola {{1}}, se ha emitido una nota de débito por {{2}} sobre la factura N° {{3}}.",
          footer: "Mydessk · Gestión de Negocios",
          buttons: [{ type: "URL", text: "Ver nota de débito" }],
          variables: ["nombre_cliente", "monto", "numero_factura"]
        },
        {
          id: -5,
          name: "retencion_emitida",
          category: "UTILITY",
          status: "approved",
          header_content: "Comprobante de retención",
          body:
            "Hola {{1}}, hemos emitido el comprobante de retención N° {{2}} por un valor de {{3}}.",
          footer: "Mydessk · Gestión de Negocios",
          buttons: [{ type: "URL", text: "Ver retención" }],
          variables: ["nombre_cliente", "numero_retencion", "monto"]
        },
        {
          id: -6,
          name: "liquidacion_emitida",
          category: "UTILITY",
          status: "approved",
          header_content: "Liquidación de compra",
          body:
            "Hola {{1}}, te enviamos la liquidación de compra N° {{2}} por {{3}}.",
          footer: "Mydessk · Gestión de Negocios",
          buttons: [{ type: "URL", text: "Ver liquidación" }],
          variables: ["nombre_cliente", "numero_liquidacion", "monto"]
        },
        {
          id: -7,
          name: "remision_emitida",
          category: "UTILITY",
          status: "approved",
          header_content: "Guía de remisión",
          body:
            "Hola {{1}}, hemos emitido la guía de remisión N° {{2}} para {{3}}.",
          footer: "Mydessk · Gestión de Negocios",
          buttons: [{ type: "URL", text: "Ver guía" }],
          variables: ["nombre_cliente", "numero_remision", "destinatario"]
        },
        {
          id: -8,
          name: "pago_recibido",
          category: "UTILITY",
          status: "approved",
          header_content: "Pago confirmado",
          body:
            "Hola {{1}}, hemos recibido tu pago de {{2}} correspondiente a la factura {{3}}. ¡Gracias!",
          footer: "Mydessk · Gestión de Negocios",
          buttons: [],
          variables: ["nombre_cliente", "monto", "numero_factura"]
        },
        {
          id: -9,
          name: "recordatorio_vencimiento",
          category: "MARKETING",
          status: "approved",
          header_content: "",
          body:
            "Hola {{1}}, te recordamos que tu factura {{2}} por {{3}} vence en {{4}} días. ¿Necesitas ayuda con el pago?",
          footer: "",
          buttons: [{ type: "QUICK_REPLY", text: "Necesito ayuda" }],
          variables: [
            "nombre_cliente",
            "numero_factura",
            "monto",
            "dias_vencida"
          ]
        }
      ];
    },

    suggestTemplateForModule() {
      if (!this.moduleType || !this.templates.length) return null;
      const aliasMap = {
        quotes: "quote",
        invoices: "invoice",
        "credit-notes": "credit-note",
        "debit-notes": "debit-note",
        withholdings: "withholding",
        liquidations: "liquidation",
        remissions: "remission"
      };
      const key = aliasMap[this.moduleType] || this.moduleType;
      // Palabras clave por módulo para detectar la plantilla más relevante
      // por nombre o por encabezado del cuerpo.
      const keywords = {
        quote: ["cotizacion", "cotizaci"],
        invoice: ["factura", "factur"],
        "credit-note": ["nota_credito", "nota credito", "credito"],
        "debit-note": ["nota_debito", "nota debito", "debito"],
        withholding: ["retencion"],
        liquidation: ["liquidacion"],
        remission: ["remision", "guia"]
      };
      const keys = keywords[key];
      if (!keys) return null;
      const norm = s =>
        String(s || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "");
      return this.templates.find(t => {
        const haystack =
          norm(t.name) + " " + norm(t.header_content) + " " + norm(t.body);
        return keys.some(k => haystack.includes(k));
      });
    },

    onTemplateChange() {
      const tpl = this.selectedTemplate;
      if (!tpl) {
        this.variableFields = [];
        this.emitValue();
        return;
      }

      const body = tpl.body || "";
      const matches = [...body.matchAll(/\{\{(\d+)\}\}/g)];
      const nums = matches.map(m => parseInt(m[1]));
      const count = nums.length ? Math.max(...nums) : 0;

      const varNames = tpl.variables || [];
      const old = this.variableFields;
      const tokens = this.availableTokens;
      // Plantilla ya guardada: mostrar sus variables tal como se guardaron
      const saved =
        !old.length &&
        this.value &&
        this.value.templateId === tpl.id &&
        Array.isArray(this.value.variables)
          ? this.value.variables
          : null;

      this.variableFields = Array.from({ length: count }, (_, i) => {
        const label = varNames[i] || "";
        if (saved && saved[i] !== undefined) {
          const isToken = tokens.some(t => t.value === saved[i]);
          return {
            label,
            value: isToken ? saved[i] : "__custom__",
            customValue: isToken ? "" : saved[i]
          };
        }
        // Si ya había un valor previo, conservarlo
        if (old[i] && old[i].value) {
          return {
            label,
            value: old[i].value,
            customValue: old[i].customValue || ""
          };
        }
        // Pre-seleccionar token según el label
        let guessed = guessTokenFromLabel(label, tokens);
        if (!guessed && !label && this.source === "marketing") {
          guessed = this.guessTokenFromContext(body, i + 1, tokens, i);
        }
        return {
          label,
          value: guessed || (tokens[0] ? tokens[0].value : "__custom__"),
          customValue: ""
        };
      });

      this.emitValue();
    },

    // Plantillas de marketing no traen nombre de variable: se deduce del texto
    // que va justo antes de {{n}} ("Hola {{1}}", "la factura {{2}}", "venció el
    // {{4}}") y, si no hay pista, por la posición típica de un cobro.
    guessTokenFromContext(body, n, tokens, index) {
      const at = body.indexOf(`{{${n}}}`);
      const before = at >= 0 ? body.slice(0, at) : "";
      const context = before
        .slice(-30)
        .split(/\}\}/)
        .pop()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      const has = t => tokens.some(x => x.value === t);
      const rules = [
        {
          re: /(hola|estimad[oa]|senor[a]?|sr\.?|sra\.?)\s*$/,
          token: "{{nombre_cliente}}"
        },
        {
          re: /(factura|comprobante|documento)\s*(n[°o.]*|#|numero|no\.)?\s*$/,
          token: "{{numero_factura}}"
        },
        {
          re: /(vencio|vence|vencimiento|hasta|fecha)( el| del)?\s*$/,
          token: "{{fecha_vencimiento}}"
        },
        { re: /(\$|por|monto|valor|total|de)\s*$/, token: "{{monto}}" },
        { re: /(hace|lleva|tiene)\s*$/, token: "{{dias_vencida}}" }
      ];
      for (const r of rules) {
        if (r.re.test(context) && has(r.token)) return r.token;
      }
      const positional = [
        "{{nombre_cliente}}",
        "{{numero_factura}}",
        "{{monto}}",
        "{{fecha_vencimiento}}",
        "{{dias_vencida}}"
      ];
      return has(positional[index]) ? positional[index] : null;
    },
    emitValue() {
      const tpl = this.selectedTemplate;
      // Resuelve cada variable: si es token del sistema → manda el token,
      // si es "__custom__" → manda el customValue, si es vacío → string vacío.
      // El backend reemplaza los tokens por el valor real de cada destinatario.
      const variables = this.variableFields.map(f => {
        if (f.value === "__custom__") return f.customValue || "";
        return f.value || "";
      });
      this.$emit(
        "input",
        tpl
          ? {
              templateId: tpl.id,
              templateName: tpl.name,
              templateLanguage: tpl.language || null,
              variables,
              template: tpl
            }
          : null
      );
    },

    varLabel(i) {
      return "\x7b\x7b" + (i + 1) + "\x7d\x7d";
    },

    // Valor de ejemplo para mostrar en el preview — el destinatario verá su
    // dato real, esto es solo referencia visual.
    exampleValueForToken(token) {
      const examples = {
        "{{nombre_cliente}}": "Juan Pérez",
        "{{email_cliente}}": "juan@ejemplo.com",
        "{{telefono_cliente}}": "+593999111222",
        "{{empresa}}": "Mi Empresa S.A.",
        "{{nombre_empresa}}": "Mi Empresa S.A.",
        "{{ruc_empresa}}": "1791234567001",
        "{{telefono_empresa}}": "+593222333444",
        "{{numero_cotizacion}}": "COT-2026-0042",
        "{{numero_factura}}": "FAC-2026-0128",
        "{{numero_retencion}}": "RET-001",
        "{{numero_liquidacion}}": "LIQ-001",
        "{{numero_remision}}": "GR-2026-0007",
        "{{destinatario}}": "Juan Pérez",
        "{{monto}}": "$500.00",
        "{{fecha}}": new Date().toLocaleDateString("es-EC", {
          year: "numeric",
          month: "long",
          day: "numeric"
        }),
        "{{fecha_vencimiento}}": "30/06/2026",
        "{{dias_vencida}}": "5"
      };
      return examples[token] || token;
    },

    setAsDefault() {
      if (!this.localKey || !this.selectedId) return;
      localStorage.setItem(this.localKey, this.selectedId);
      this.defaultId = this.selectedId;
    },

    unsetDefault() {
      if (!this.localKey) return;
      localStorage.removeItem(this.localKey);
      this.defaultId = null;
    },

    goToMarketing() {
      // app.mydessk.com → marketing.mydessk.com (mismo criterio que NavbarItems)
      const host = window.location.host;
      const url = host.includes("app.mydessk.com")
        ? "https://" + host.replace("app.mydessk.com", "marketing.mydessk.com")
        : "https://marketing.mydessk.com";
      window.open(url, "_blank", "noopener");
    },
    goToTemplates() {
      this.$router
        .push({
          path: "/dashboard/configurations",
          query: { tab: "whatsapp" }
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.wa-tpl-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.wa-tpl-picker__mock-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4;
}
.wa-tpl-picker__mock-banner i {
  font-size: 13px;
  flex-shrink: 0;
}
.wa-tpl-picker__loading,
.wa-tpl-picker__empty {
  font-size: 13px;
  color: #6b7280;
  padding: 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.wa-tpl-picker__link {
  color: #3ecfb4;
  text-decoration: none;
  font-size: 12px;
}
.wa-tpl-picker__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wa-tpl-picker__label {
  font-size: 11px;
  font-weight: 700;
  color: #31355c;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.wa-tpl-picker__default-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
}
.wa-tpl-picker__default-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #d97706;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 3px 10px;
}
.wa-tpl-picker__default-badge i {
  color: #f59e0b;
  font-size: 13px;
}
.wa-tpl-picker__default-link {
  margin-left: 8px;
  color: #6b7280;
  font-size: 10px;
  font-weight: 500;
  text-decoration: underline;
}
.wa-tpl-picker__default-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6b7280;
  text-decoration: none;
  padding: 3px 0;
  transition: color 0.15s;
}
.wa-tpl-picker__default-action:hover {
  color: #f59e0b;
}
.wa-tpl-picker__default-action i {
  font-size: 13px;
}
.wa-tpl-picker__option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.wa-tpl-picker__option-name {
  font-size: 13px;
  font-weight: 600;
  color: #31355c;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wa-tpl-picker__preview-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wa-tpl-picker__preview-caption {
  font-size: 11px;
  color: #6b7280;
  font-style: italic;
}
.wa-tpl-picker__preview {
  background: #f0fdf4;
  border: 1px solid #d1fae5;
  border-radius: 10px;
  padding: 12px;
  font-size: 13px;
  line-height: 1.5;
}
.wa-tpl-picker__preview-header {
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
}
.wa-tpl-picker__preview-body {
  color: #374151;
  white-space: pre-wrap;
}
.wa-tpl-picker__preview-footer {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 6px;
}
.wa-tpl-picker__preview-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #d1fae5;
}
.wa-tpl-picker__preview-btn {
  background: #fff;
  border: 1px solid #6ee7b7;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  color: #059669;
}

.wa-tpl-picker__vars {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.wa-tpl-picker__vars-hint {
  font-size: 11px;
  color: #6b7280;
  margin-top: -4px;
  margin-bottom: 4px;
  line-height: 1.4;
}
.wa-tpl-picker__var-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.wa-tpl-picker__var-tag {
  background: #f3f4f6;
  border-radius: 4px;
  padding: 4px 7px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
  font-family: monospace;
  line-height: 1.4;
}
.wa-tpl-picker__var-controls {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.wa-tpl-picker__var-name {
  font-size: 11px;
  color: #4b5563;
  font-weight: 500;
}
.wa-tpl-picker__var-custom {
  margin-top: 2px;
}
</style>
